import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useRouter, useSearchParams } from "next/navigation";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { useFormContext } from "react-hook-form";
import { useHandleApiCall } from "@/src/components/pages/StepPage/components/apiCalls/useHandleApiCall";
import { DataTypeEnum } from "@/src/shared/models/contextInstance";
import {
  formatContextVariablesListToFormValues,
  formatFormDataToVarList,
  safeParseJson,
} from "@/src/shared/utils/form";
import { useContextStore } from "@/src/stores/useContextStore";
import { ContextInstanceVariableType, TouristType } from "@/src/shared/schemas";
import {
  has,
  isObject,
  isString,
  last,
  size,
  some,
  sum,
  toNumber,
} from "lodash";
import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import {
  apiCallsSideEffects,
  throwStopCallsError,
} from "@/src/components/pages/StepPage/components/apiCalls/sideEffects";
import { axiosInstance, BASE_URLS } from "@/src/shared/api";
import {
  apiCallsModalStore,
  closeErrorModal,
  closeLoadingModal,
  openErrorModal,
  openLoadingModal,
} from "@/src/components/pages/StepPage/components/apiCalls/modalStore";
import { ApiCallModalType } from "@/src/components/pages/StepPage/components/apiCalls/types";
import { Button as ButtonUI } from "@/src/components/Button";
import { ParsedValue } from "@/src/components/TextWithValue";
import {
  scrollToTop,
  useApiCall,
  useNextNavigation,
  usePreviousNavigation,
  useSpecificStepNavigation,
  useUpdateProcessInstance,
} from "@/src/components/Action/useNavigation";
import useToggle from "@/src/hooks/useToggle";
import {
  COUNTRY_TRAVEL_ZONE_CONST,
  FOR_TOURISTS_CONST,
  INSURED_COUNT_VAR_LIST,
  INSURED_TOURISTS_CONST,
  PICKED_TRAVEL_CALC_CATEGORY_CONST,
  PLACE_OF_STAY_ABROAD_CONST,
  ProposalTableColumnsKeys,
  TERRORISM_CONST,
  TRAVEL_PURPOSE_CONST,
  WORK_CONST,
} from "../ProposalTable/lib/const";
import { runAction } from "@/src/api/server-actions/action";
import {
  determinePickedCategory,
  getColContextValueString,
} from "../ProposalTable/lib/utils";
import {
  actionRunningStore,
  runBeforeSubmitActions,
} from "@/src/hooks/blockly/useHandleBlocklyFields";
import { ApiCallsTypes } from "../pages/StepPage/components/apiCalls/contants";
import { premiumStore } from "@/src/stores/premiumStore";
import { CrossType } from "../CrossProductCard/types";
import { useAnalytics } from "@/src/components/pages/StepPage/components/useAnalytics";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import {
  getPlanNameReqData,
  useFormDataMiddleware,
} from "./useFormDataMiddleware";
import { getContextVariablesToApiCall } from "@/src/components/Action/utils";
import { useKeysToUpdate } from "@/src/components/Action/useKeysToUpdate";
import { useTimeSpentStore } from "@/src/stores/useTimeSpentStore";
import { openLimitZKModal } from "../pages/StepPage/components/apiCalls/Modals/modalsHandlers";
import { useTextUsModalStore } from "@/src/stores/useTextUsModalStore";
import { AnalyticsComponents } from "@/src/shared/models/analytics";
import { useMutation } from "@tanstack/react-query";
import { addErrorLog } from "@/src/api/services/integration-log";
import { isMobile, isIOS } from "react-device-detect";
import {nowInKyiv} from "@/src/shared/utils/dateTime";

const getSearchParam = (key: string) => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(key);
};

export const getUrlByDataStorageFile = async (payload: {
  uuid: string;
  fileName: string;
  sourceId: string;
}): Promise<string> => {
  const response = await axiosInstance.post(`${BASE_URLS.DATASTORAGE}/get`, {
    uuid: payload.uuid,
    originName: payload.fileName,
    sourceId: payload.sourceId,
  });
  if (response.data) {
    const fileData = new Uint8Array(response.data.data);
    const blob = new Blob([fileData], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  }
  return "";
};

export function Action(props: any) {
  const {
    methods = [],
    withActionsLoader,
    actionsLoaderTitle,
    actionsLoaderDescription,
    children,
    onClick,
    analytics,
    ...restProps
  } = props;
  const router = useRouter();

  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

  const { callAnalyticEvent, callComponentEvent } = useAnalytics();

  const updateField = useUpdateField();
  const { isToggled: isLoadingAction, setIsToggled: setLoadingAction } =
    useToggle({ initialState: false });
  const processInstance = useProcessInstanceStore(
    (state) => state.processInstance,
  );
  const { setPremium } = premiumStore();
  const { handleSubmit, formState, control, getValues, reset, trigger } =
    useFormContext();
  const { errors } = formState;
  const { handleApiCallWithSideEffects, handleApiCallByName } =
    useHandleApiCall();

  const { formDataMiddleware, cleanTouristListFromShadowItems } =
    useFormDataMiddleware();

  const { getKeysToUpdate } = useKeysToUpdate();

  const { mutateAsync: mutateNext, isPending: isNextLoading } =
    useNextNavigation({
      onSuccess: () => {
        // it will set latest values and just reset dirty and etc.
        reset(getValues());
      },
    });
  const { mutateAsync: mutatePrev, isPending: isPrevLoading } =
    usePreviousNavigation({
      onSuccess: () => {
        // it will reset value to the latest saved values after pressed 'next step'
        // and clean errors and dirty fields so that when we return on the step again
        // the logic with dirty fields that are being sent to the backend works correctly
        reset(undefined, {
          keepValues: false,
          keepDirty: false,
          keepErrors: false,
        });
      },
    });
  const { mutateAsync: mutateSpecificStep, isPending: isSpecificStepLoading } =
    useSpecificStepNavigation({
      onSuccess: (data, variables, context) => {
        const navigationType = variables?.body?.navigationType;
        if (navigationType === "NEXT") {
          reset(getValues());
        } else {
          reset(undefined, {
            keepValues: false,
            keepDirty: false,
            keepErrors: false,
          });
        }
      },
    });

  const { mutateAsync: mutateApiCall, isPending: isApiCallLoading } =
    useApiCall({
      onSuccess: async (data, _variables, context) => {
        console.log("STEP_PAGE useApiCall", {
          data,
          variables: _variables,
          context,
        });
        const variables = data.contextInstance.variables;
        if (variables) {
          const formValues = variables.reduce(
            (
              acc: Record<string, any>,
              {
                key,
                value,
                dataType,
              }: {
                key: string;
                value: string;
                dataType: DataTypeEnum;
              },
            ) => {
              acc[key] = safeParseJson(value, dataType);
              return acc;
            },
            {},
          );
          // scrollToTop();
          reset(formValues, { keepDirty: true });
        }
      },
    });

  const { mutate: addError } = useMutation({
    mutationFn: addErrorLog,
  });

  const { mutateAsync: updateProcessInstance } = useUpdateProcessInstance();

  const { getTimeSpent } = useTimeSpentStore();

  const getVariable = useContextStore((state) => state.getVariable);

  const getContextVariablesToUpdate = (
    formData: Record<string, ContextInstanceVariableType["value"]>,
    dirtyFields: Record<string, boolean>,
  ) => {
    console.log("dirtyFields", dirtyFields);
    console.log("formData", formData);
    const contextVariablesToUpdate = Object.keys(dirtyFields || {}).reduce(
      (acc, key) => {
        const dirtyFieldValue = dirtyFields[key];
        const contextVariableToUpdate = getVariable(key);
        if (
          dirtyFieldValue &&
          contextVariableToUpdate &&
          has(formData, key) &&
          contextVariableToUpdate.readOnly === false
        ) {
          const newValue = parseContextVariableValue(formData[key], {
            ...contextVariableToUpdate,
            dataType: contextVariableToUpdate.dataType,
            key,
          });
          if (isString(newValue)) {
            acc.push({
              ...contextVariableToUpdate,
              value: newValue,
            });
          }
        }

        return acc;
      },
      [] as ContextInstanceVariableType[],
    );
    return contextVariablesToUpdate;
  };

  const getAllContextVariablesToUpdate = (
    formData: Record<string, ContextInstanceVariableType["value"]>,
  ) => {
    const contextVariablesToUpdate = Object.keys(formData || {}).reduce(
      (acc, key) => {
        const contextVariableToUpdate = getVariable(key);
        if (contextVariableToUpdate) {
          const newValue = parseContextVariableValue(formData[key], {
            dataType: contextVariableToUpdate.dataType,
            key,
          });
          if (isString(newValue)) {
            acc.push({
              ...contextVariableToUpdate,
              value: newValue,
            });
          }
        }
        return acc;
      },
      [] as ContextInstanceVariableType[],
    );
    return contextVariablesToUpdate;
  };

  const handleUpdateProcessInstance = async (
    formData: Record<string, ContextInstanceVariableType["value"]>,
  ) => {
    if (processInstanceId) {
      const recordData = formDataMiddleware(null, formData);
      const payload = {
        id: processInstance.id,
        order: processInstance.order,
        processId: processInstance.processId,
        activeStepId: processInstance.activeStepId,
        contextInstance: {
          ...processInstance.contextInstance,
          variables: getAllContextVariablesToUpdate(recordData),
        },
      };
      await updateProcessInstance(payload);
    }
  };

  const handlePrev = async () => {
    if (processInstanceId) {
      await mutatePrev({
        processInstanceId,
        timeSpent: getTimeSpent(),
      });
    }
  };

  const handleNext = async (
    formData: Record<string, ContextInstanceVariableType["value"]>,
  ) => {
    if (processInstanceId) {
      const recordData = formDataMiddleware(null, formData);
      const payload = {
        processInstanceId,
        contextVariablesToUpdate: getContextVariablesToUpdate(
          recordData,
          getKeysToUpdate(),
        ),
        timeSpent: getTimeSpent(),
      };
      await mutateNext(payload);
    }
  };

  const handleSpecificStep = async (
    formData: any,
    params: { stepId: string; navigationType: "NEXT" | "PREVIOUS" },
  ) => {
    const { stepId, navigationType } = params;
    if (processInstanceId) {
      const recordData = formDataMiddleware(null, formData);
      await mutateSpecificStep({
        code: stepId,
        body: {
          processInstanceId,
          contextVariablesToUpdate: getContextVariablesToUpdate(
            recordData,
            getKeysToUpdate(),
          ),
          timeSpent: getTimeSpent(),
          navigationType,
        },
      });
    }
  };

  const handleApiCall = async (
    formData: Record<string, unknown>,
    params: {
      actionName: string;
      actionParams: any[];
      mapper: Record<string, string | null>;
    },
  ) => {
    console.log("api call formData", formData);
    const { actionName, actionParams, mapper } = params;

    if (actionName.toLowerCase().includes("lead") && processInstanceId) {
      addError({
        processInstanceId,
        actionName: "frontendCallLead",
        message: `Lead action is ${actionName}`,
        statusCode: 200,
        requestMethod: "POST",
      });
    }

    if (processInstanceId && processInstance) {
      const recordData = formDataMiddleware(actionName, formData);
      const apiCallVariables = getContextVariablesToApiCall({
        formData: recordData,
        params: actionParams,
        mapper,
        keyToUpdate: getKeysToUpdate(),
      });
      await mutateApiCall({
        actionName,
        body: {
          id: processInstance.id,
          processId: processInstance.processId,
          activeStepId: processInstance.activeStepId,
          contextInstance: {
            id: processInstance.contextInstance.id,
            variables: apiCallVariables,
          },
        },
      });
    }
  };

  const isMethodPresent = (
    methods: any[],
    methodName: string,
    strict = true,
  ) => {
    return some(methods, (method) =>
      strict
        ? method?.method === methodName
        : method?.method.includes(methodName),
    );
  };

  const handleClick = async () => {
    actionRunningStore.getState().setIsActionRunning(true);
    const prevProcessInstance = processInstanceId;

    try {
      analytics && callAnalyticEvent(analytics);
      setLoadingAction(true);
      if (withActionsLoader) {
        openLoadingModal({
          title: actionsLoaderTitle ?? "",
          description: actionsLoaderDescription ?? "",
          disableClose: true,
        });
      }
      // if (
      //   methods.some(
      //     ({ method }: any) => method === "documents_approve" || method.includes("navigation_next"),
      //   )
      // ) {
      //   console.log('methods some', methods)
      //   await handleUpdateProcessInstance(getValues());
      // }
      const submit = async () => {
        await runBeforeSubmitActions(getValues());
        if (!Object.values(control._formState.errors).length) {
          const isValid = await trigger();
          await handleSubmit(() => {})();
          if (!isValid) {
            scrollToTop(true);
          }
          return isValid;
        } else {
          const isValid = await trigger();
          if (!isValid) {
            scrollToTop(true);
          }
          return false;
        }
      };
      if (isMethodPresent(methods, "travelPlansCostCalculator")) {
        const isValid = await trigger();
        console.log("FormError: ", errors);
        if (!isValid) {
          scrollToTop(true);
          return;
        }
      }
      if (isMethodPresent(methods, "documents_approve")) {
        await updateField("issueDateOfContract", nowInKyiv().toISO());
        await handleSubmit(
          (...data) => {
            console.log("SUBMIT SUCCESS", data);
          },
          (...data) => {
            scrollToTop(true);
            console.log("SUBMIT ERROR", data);
          },
        )();
      }
      const prevPremium = getValues("finalPremium");
      setPremium(prevPremium);

      for (const method of methods) {
        const methodName = method.method;
        const params = method.params;

        if (methodName === "force_submit") {
          const isValid = await submit();
          if (!isValid) return;
        }
        if (methodName === "navigation_next") {
          const isValid = await submit();
          console.log("formState", formState);
          if (isValid) {
            await handleNext(getValues());
          }
        }
        if (methodName === "navigation_prev") {
          await handlePrev();
        }
        if (methodName === "navigation_step") {
          const navigationType = params.navigationType;
          if (navigationType === "NEXT") {
            const isValid = await submit();
            if (isValid) {
              await handleSpecificStep(getValues(), params);
            }
            return;
          }
          await handleSpecificStep(getValues(), params);
        }
        if (methodName === "apicall") {
          await trigger();
          const isValid = !size(formState.errors);
          console.log("formstatae apicall", formState.errors);
          let allowCall = isValid;
          // All actions that are related to Lead should be allowed to call if the auto need PVI or not, and also after the unsuccessful condition after the need for PVI.
          if (
            [
              "validatePromocode",
              "AutozahistLeadAction",
              "CheckNeedPviAction",
              "AutozahistTariffCalculator",
            ].some((item) => item.includes(params?.actionName)) &&
            formState.errors?.is_vehicle_allowed
          ) {
            allowCall = true;
          }
          if (allowCall) {
            params.actionName in apiCallsSideEffects
              ? await handleApiCallWithSideEffects(
                  getValues(),
                  params,
                  method?.additionalParams,
                )
              : await handleApiCall(getValues(), params);
          } else {
            scrollToTop(true);
          }
        }

        if (methodName === "tourists_check") {
          await trigger();
          const isValid = !size(formState.errors);
          const touristsList = (
            (getValues(FOR_TOURISTS_CONST) || []) as TouristType[]
          ).map((tourist) =>
            Object.fromEntries(
              Object.entries(tourist).map(([key, value]) => [
                key.replace("tourist_", ""),
                value,
              ]),
            ),
          );
          const touristFields = Object.keys(touristsList[0]).concat([
            "middleName",
          ]);
          const initialValues = getValues(touristFields);
          console.log("touristsList", touristsList);

          const finapCheck = () => {
            return touristsList.map((tourist) =>
              handleApiCallByName(ApiCallsTypes.PERFORM_FINAP_ACTION, false, {
                formDataOverride: {
                  ...getValues(),
                  ...tourist,
                  middleName: null,
                  forTourists: getValues(FOR_TOURISTS_CONST),
                },
              }),
            );
          };
          const ordloCheck = () => {
            return touristsList.map((tourist) =>
              handleApiCallByName(ApiCallsTypes.CHECK_ORDLO, false, {
                formDataOverride: {
                  ...getValues(),
                  ...tourist,
                  forTourists: getValues(FOR_TOURISTS_CONST),
                },
                mapper: {
                  checkORDLOCode: "city",
                },
                paramsOverride: [
                  {
                    key: "checkORDLOCode",
                    options: {
                      type: "STRING",
                      required: true,
                      isArray: false,
                    },
                  },
                ],
              }),
            );
          };
          const blackListCheck = () => {
            return touristsList.map((tourist) =>
              handleApiCallByName(ApiCallsTypes.CHECK_BLACK_LIST, false, {
                formDataOverride: {
                  ...getValues(),
                  ...tourist,
                  forTourists: getValues(FOR_TOURISTS_CONST),
                },
                mapper: { givenName: "firstName", surname: "lastName" },
              }),
            );
          };
          if (isValid) {
            const res = await Promise.allSettled(finapCheck());
            const res2 = await Promise.allSettled(ordloCheck());
            const res3 = await Promise.allSettled(blackListCheck());
            console.log("res, res2, res3", res, res2, res3);
            if (
              res.some((r) => r.status === "rejected") ||
              res2.some((r) => r.status === "rejected") ||
              res3.some((r) => r.status === "rejected")
            ) {
              const insurerData = touristFields.reduce(
                (acc, key, i) => {
                  const value = initialValues[i];
                  if (value === undefined) {
                    return acc;
                  }

                  return [...acc, { key, value }];
                },
                [] as { key: string; value: any }[],
              );
              insurerData.forEach(({ key, value }) => {
                updateField(key, value);
              });
              return;
            }
          }
        }

        if (methodName === "travelPlansCostCalculator") {
          const calculateTravelPlans = async () => {
            const { actionParams } = params || {};
            const insuredTourists = getValues(INSURED_TOURISTS_CONST) as Record<
              string,
              number
            >;
            if (!actionParams || !isObject(insuredTourists)) {
              return;
            }

            const apiCallActionParams = {
              countryTravelZone: getValues(COUNTRY_TRAVEL_ZONE_CONST),
              work: getValues(WORK_CONST),
              daysAbroad:
                getValues("travelType")?.value === "one-time"
                  ? toNumber(getValues("daysAbroad-oneTimeTrip"))
                  : toNumber(getValues("daysAbroad-multiTimeTrip")?.value),
              terrorism: getValues(TERRORISM_CONST),
              travelPurpose: getValues(TRAVEL_PURPOSE_CONST),
              placeOfStayAbroad: getValues(PLACE_OF_STAY_ABROAD_CONST),
            };

            const ageVariables = INSURED_COUNT_VAR_LIST.reduce<
              Record<string, number>
            >((acc, touristKey) => {
              acc[touristKey] = insuredTourists[touristKey];
              return acc;
            }, {});
            const listOfRequests: Array<{ plan: string; response: any }> = [];
            const forTourists =
              cleanTouristListFromShadowItems(getValues(FOR_TOURISTS_CONST)) ??
              [];

            const pickedCategoryAccordingDaysAbroad = determinePickedCategory({
              category: getValues(PICKED_TRAVEL_CALC_CATEGORY_CONST),
              daysAbroad: toNumber(apiCallActionParams.daysAbroad),
            });

            for (const key in ProposalTableColumnsKeys) {
              const planName =
                ProposalTableColumnsKeys[
                  key as keyof typeof ProposalTableColumnsKeys
                ];

              const staticValueKey = getColContextValueString(planName);
              const planNameReqData = getPlanNameReqData(planName, getValues());

              if (!planNameReqData) {
                openErrorModal({
                  title: "Сталася помилка",
                  description: "Шановний клієнте, під час обробки вашого замовлення сталась технічна помилка.\n" +
                    "Будь ласка, зателефонуйте за контактами інтернет-магазину або скористайтесь опцією «Написати нам».\n" +
                    "Перепрошуємо за незручності та дякуємо за розуміння!",
                  submitText: "Написати",
                  onSubmit: () => {
                    closeErrorModal();
                    useTextUsModalStore.getState().open();
                  }
                });
                throw new Error("Plan name not found");
              }
              const insuredTourists = getValues("insuredTourists");

              const variables = {
                ...planNameReqData,
                [staticValueKey]: planNameReqData,
                ...apiCallActionParams,
                ...ageVariables,
                calculationCategory: planName,
                pickedTravelCalculationCategory:
                  pickedCategoryAccordingDaysAbroad,
                [FOR_TOURISTS_CONST]: forTourists,
                insuredCount: sum(Object.values(insuredTourists)),
                insuredTourists,
              };

              const formattedVariables = formatFormDataToVarList(variables);

              const requestBody: any = {
                id: processInstanceId,
                processId: processInstance.processId,
                activeStepId: processInstance.activeStepId,
                contextInstance: {
                  id: processInstance.contextInstance.id,
                  variables: formattedVariables,
                },
              };

              try {
                const response = await runAction({
                  actionName: ApiCallsTypes.TRAVEL_TARIFF_CALCULATOR,
                  body: requestBody,
                });
                listOfRequests.push({ plan: planName, response });
              } catch (error) {
                console.error(`Error processing plan: ${planName}`, error);
                throw new Error("Failed to calculate");
              }
            }

            const latestTariffResponse = last(listOfRequests)?.response;
            if (!latestTariffResponse) {
              throw new Error("No response available for processing.");
            }

            const formValues = formatContextVariablesListToFormValues(
              latestTariffResponse.contextInstance.variables,
            );

            const formattedKeys = Object.keys(ProposalTableColumnsKeys).reduce<
              Record<string, any>
            >((acc, planKey) => {
              const planName =
                ProposalTableColumnsKeys[
                  planKey as keyof typeof ProposalTableColumnsKeys
                ];
              acc[`${planName}_premium`] = formValues[`${planName}_premium`];
              return acc;
            }, {});

            const staticValueKey = pickedCategoryAccordingDaysAbroad
              ? getColContextValueString(pickedCategoryAccordingDaysAbroad)
              : "Business_req_data";
            const columnStaticValues = staticValueKey
              ? getValues(staticValueKey)
              : {};

            const mergedValues = {
              ...getValues(),
              ...formValues,
              ...formattedKeys,
              ...columnStaticValues,
              calculationCategory: formValues.calculationCategory,
              pickedTravelCalculationCategory:
                formValues.pickedTravelCalculationCategory,
              [FOR_TOURISTS_CONST]: formValues[FOR_TOURISTS_CONST],
            };

            reset(mergedValues, { keepDirty: true, keepDirtyValues: true });
          };
          await calculateTravelPlans();
        }
        if (methodName === "documents_approve") {
          // const openLink = async () => {
          //   const payload = getValues("offerPrintout");
          //   if (!payload) return
          //   const fileURL = await getUrlByDataStorageFile(payload);
          //   setTimeout(() => window.open(fileURL, "_blank", "noopener,noreferrer"), 0);
          // }
          const isValid = await submit();
          const datesValid = !!getValues("startDate") && !!getValues("endDate");
          if (isValid && datesValid) {
            if (
              prevProcessInstance === getSearchParam(PROCESS_INSTANCE_QUERY)
            ) {
              apiCallsModalStore.getState().open(ApiCallModalType.AGREEMENT, {
                description: method.additionalParams.description,
                // onLinkClick: openLink,
              });
            }
          } else {
            scrollToTop(true);
          }
        }

        if (methodName === "crosses_check") {
          const isValid = await submit();
          if (isValid) {
            const pickedCrosses = getValues("pickedCrosses");
            const isLegkiyCrossAllowed =
              getValues("vehicleType")?.value?.includes("B");
            const legkiyRegex = new RegExp(
              `^${CrossType.LegkyiZahyst}Premium_(\\d+)$`,
            );

            const isCrossesInvalid =
              pickedCrosses.some((cross: string) => cross.match(legkiyRegex)) &&
              !isLegkiyCrossAllowed;

            if (isCrossesInvalid) {
              apiCallsModalStore.getState().open(ApiCallModalType.ERROR, {
                description: `Для обраного типу транспортного засобу неможливо придбати додатковий продукт на розширення ліміту відповідальності СВ+НВ, що був обраний Вами раніше\nДля укладання договору страхування з урахуванням внесених змін, потрібно перейти на крок «Розрахунок»`,
                onSubmit: () => {
                  router.push("oscpv");
                  apiCallsModalStore.getState().close();
                },
                submitText: "До розрахунку",
                title: "\u00A0",

                withCross: true,
              });
              throw new Error("Crosses are invalid");
            }
          } else {
            scrollToTop(true);
          }
        }

        if (methodName === "download_policy") {
          const policyPrintouts = getValues("policyPrintouts")?.filter(Boolean);
          const contractNumber = getValues("contractNumber");

          if (!policyPrintouts) return;

          if (isMobile && isIOS) {
            const zip = new JSZip();

            for (const payload of policyPrintouts) {
              const fileURL = await getUrlByDataStorageFile(payload);
              const response = await fetch(fileURL);
              const blob = await response.blob();
              zip.file(payload.fileName, blob); // Добавляем файл в архив
            }

            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(
              zipBlob,
              contractNumber ? `${contractNumber}.zip` : "policies.zip",
            );
          } else {
            for (const payload of policyPrintouts) {
              const fileURL = await getUrlByDataStorageFile(payload);

              const downloadLink = document.createElement("a");
              downloadLink.href = fileURL;
              downloadLink.download = payload.fileName;
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
            }
          }
        }

        if (methodName === "check_premium_limit") {
          const finalPremium = getValues("finalPremium") || 0;

          if (Number(finalPremium) > 40000) {
            callComponentEvent &&
              callComponentEvent(AnalyticsComponents.ZK_MAX_LIMIT);
            openLimitZKModal({
              onCancel: () => {
                window.open(
                  "https://www.pzu.com.ua/useful/filials.html",
                  "_blank",
                );
                apiCallsModalStore.getState().close();
              },
              onSubmit: () => {
                apiCallsModalStore.getState().close();
                useTextUsModalStore.getState().open();
              },
            });
            throwStopCallsError();
            return;
          }
        }
      }
      onClick?.();
    } catch (error) {
      console.error("An error occurred during sequential execution:", error);
    } finally {
      if (withActionsLoader) {
        closeLoadingModal();
      }
      setLoadingAction(false);
      actionRunningStore.getState().setIsActionRunning(false);

      console.log("ALL REQUESTS ARE DONE");
    }
  };

  const params = methods?.[0]?.params;
  const orderStatus = processInstance?.order?.status;
  // const disabled = false;
  return (
    <ButtonUI
      {...restProps}
      onClick={handleClick}
      loading={
        isNextLoading ||
        isPrevLoading ||
        isSpecificStepLoading ||
        isApiCallLoading ||
        isLoadingAction
      }
      href={params?.href}
      target={params?.target}
      disabled={
        restProps.disabled ||
        ((isMethodPresent(methods, "documents_approve") ||
          isMethodPresent(methods, "navigation_", false)) &&
          ["BILLING_IN_PROGRESS", "ERROR"].includes(orderStatus))
      }
    >
      <ParsedValue value={children as string} maskType={restProps?.maskType} />
    </ButtonUI>
  );
}
Action.displayName = "Button";
